import React, { useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/Init';
import useEntitlement from '../../hooks/useEntitlement';
import AurumButton from '../oracle/AurumButton';
import { COPY } from '../../contracts/copyMap';
import { ROUTES } from '../../contracts/routes';

// WHAT OTHERS SAW — the peer readout on Home.
//
// ── THIS IS THE DESIGN.md EXCEPTION ─────────────────────────────────────────
//
// DESIGN.md makes the peer layer positive-only: "no negative reactions — the
// affordance set is positive-only by design". This surface breaks that, and
// the exception is scoped as narrowly as it can be:
//
//   * AGGREGATE counts only, never an individual verdict
//   * the owner's OWN scan only
//   * never on the judging surface itself
//   * never in red — counts render in Aurum, which the allow-list permits for
//     metric values
//
// The Glowup encouragement deck keeps its positive-only rule untouched.
//
// Renders NOTHING when nobody has read the scan yet. Home degrades block by
// block, and an empty "what others saw" panel is worse than no panel: it
// implies people looked and had nothing to say.
//
// ── USED IN TWO PLACES, WHICH IS THE POINT ──────────────────────────────────
//
// Home renders one of these for the latest scan. The Vault's FEEDBACK ▸
// RECEIVED view renders one PER SCAN, which is the only way feedback on an
// older scan is reachable at all. Both must show the same numbers under the
// same lock, so this stays the single implementation and the two callers
// differ only in framing:
//
//   heading    the row's label. Home names the section; the Vault card names
//              the SCAN (its date), because in a list "WHAT OTHERS SAW" five
//              times over says nothing about which scan is which.
//   className  Home needs its own top margin; a list item does not.
export default function FeedbackSection({
  scanId,
  feedback,
  heading = COPY.FEEDBACK_SECTION_LABEL,
  className = 'mt-8 lg:mt-10',
}) {
  const seenRef = useRef(null);
  const { isPro, error: entitlementError } = useEntitlement();

  // Acknowledge on mount so the scan stops counting as unread. Fired once per
  // scan_id, not once per render — Home re-renders on every history or routine
  // settle. Deliberately unawaited: this is an acknowledgement, and a failed
  // one costs nothing now that email fires on the reading itself rather than
  // on this flag (see emailFunctions/onFeedbackReceived).
  //
  // The rejection is caught on the PROMISE, not in a try/catch. A try/catch
  // around an unawaited call catches only a synchronous throw, so every failed
  // acknowledgement used to surface as an unhandled rejection — and the Vault's
  // RECEIVED view renders one of these per scan, which turned one stray
  // rejection into a screenful.
  useEffect(() => {
    if (!scanId || !feedback || feedback.unseen_count <= 0) return;
    if (seenRef.current === scanId) return;
    seenRef.current = scanId;
    Promise.resolve()
      .then(() => httpsCallable(functions, 'markFeedbackSeen')({ scan_id: scanId }))
      .catch((err) => console.warn('FeedbackSection: markFeedbackSeen failed', err));
  }, [scanId, feedback]);

  if (!feedback || !feedback.responses_count) return null;

  const { strengths = [], improvements = [], responses_count: count } = feedback;
  const unit = count === 1 ? COPY.FEEDBACK_COUNT_ONE : COPY.FEEDBACK_COUNT_MANY;
  // One scale for both columns — see Column.
  const max = [...strengths, ...improvements]
    .reduce((m, r) => Math.max(m, Number(r.count) || 0), 0);

  // ── THE BLUR IS A TEASER, NOT A GATE ──────────────────────────────────────
  //
  // ⚠ The counts are STILL IN THE PAYLOAD. listVaultEntries returns
  // `peer_feedback` to the scan owner unconditionally (vaultFunctions/
  // listVaultEntries.js — `isProUser` there feeds the unlock logic, not this),
  // so anyone who opens the network tab reads their own numbers. CLAUDE.md's
  // rule against client-side gates over payloads that carry the data applies
  // to the VERDICT, which really is withheld server-side; this is deliberately
  // the softer thing — showing the shape of what is behind the door is the
  // whole point of a teaser, and it cannot be done without the data.
  //
  // If this is ever meant to be a real paywall, listVaultEntries has to stop
  // returning peer_feedback to non-pro owners, and this component then needs
  // placeholder rows to blur instead of real ones.
  //
  // FAILS OPEN, unlike requireEntitlement. A snapshot error leaves isPro false,
  // and blurring a paying member's own feedback during a Firestore blip costs
  // a real member a real thing; the opposite error costs nothing, because the
  // numbers are already sitting in that person's own network response.
  const locked = !isPro && !entitlementError;

  const columns = (
    <div className="mt-4 grid grid-cols-2 gap-5 lg:gap-8">
      <Column label={COPY.FEEDBACK_STRENGTHS_LABEL} rows={strengths} max={max} testId="feedback-strengths" />
      <Column label={COPY.FEEDBACK_IMPROVEMENTS_LABEL} rows={improvements} max={max} testId="feedback-improvements" />
    </div>
  );

  return (
    <section className={className} data-testid="feedback-section">
      {/* Header stays sharp. The count is the honest half of the teaser — how
          many people read the scan is not what is being sold; what they SAID
          is. Blurring it too would hide the reason to care. */}
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="font-display uppercase text-xs tracking-[0.24em] text-[#E8E2D6]">
          {heading}
        </h2>
        <span className="font-display text-[11px] tracking-[0.16em] text-bone-dim tabular-nums">
          {`${count} ${unit}`}
        </span>
      </div>

      {!locked && columns}

      {locked && (
        <div className="relative" data-testid="feedback-locked">
          {/* aria-hidden AND inert: a screen reader would otherwise read out
              the very rows the blur is withholding, and a keyboard user would
              tab through bars they cannot see. */}
          <div
            aria-hidden="true"
            data-testid="feedback-blurred"
            // eslint-disable-next-line react/no-unknown-property
            inert=""
            // Tuned by eye at 375px: 7px of blur under a 45% scrim reduced the
            // rows to dark smudges, which sells nothing. 5px over a 25% scrim
            // keeps the two columns, the bar lengths and the gold counts
            // legible as SHAPES while no label stays readable.
            className="pointer-events-none select-none blur-[5px] opacity-90"
          >
            {columns}
          </div>

          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3.5 rounded-card bg-obsidian/25 px-5 text-center">
            <p className="m-0 max-w-[19rem] font-display text-[14px] text-bone">
              {COPY.FEEDBACK_LOCKED_BODY}
            </p>
            <AurumButton
              to={ROUTES.MEMBERSHIP}
              className="shadow-aurum-glow"
              data-testid="feedback-upsell"
            >
              {COPY.FEEDBACK_LOCKED_CTA}
            </AurumButton>
          </div>
        </div>
      )}
    </section>
  );
}

// Each trait is a bar, scaled against the busiest trait ACROSS BOTH COLUMNS.
//
// One scale, not one per column. Both columns count the same unit — how many
// of this scan's judges named that trait — so per-column scaling would draw a
// 6 the same length as a 14 and assert an equality that is simply false. The
// cost is that a quiet column renders short, which is the honest outcome: it
// WAS quiet. The 6% floor below keeps its rows visible.
//
// The count stays printed beside every bar. The bar is the shape; the number
// is the fact.
function Column({ label, rows, max, testId }) {
  return (
    <div data-testid={testId}>
      <div className="font-display uppercase text-[10px] tracking-[0.2em] text-bone-dim pb-2.5 border-b border-hairline-soft">
        {label}
      </div>
      <ul className="mt-3 flex flex-col gap-3 list-none p-0 m-0">
        {rows.length === 0 && (
          <li className="font-display italic text-bone-dim text-xs">—</li>
        )}
        {rows.map((row) => {
          const count = Number(row.count) || 0;
          // Floored at 6% so a trait somebody actually named still draws a
          // visible mark next to a runaway leader, instead of reading as zero.
          const pct = max > 0 ? Math.max(6, Math.round((count / max) * 100)) : 0;
          return (
            <li key={row.key} data-testid="feedback-row">
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-display uppercase text-[11px] tracking-[0.14em] text-bone truncate">
                  {row.label}
                </span>
                {/* Aurum on a metric value is a sanctioned use per DESIGN.md
                    §2.1. Improvement counts use the SAME colour as strengths —
                    nothing on this surface renders as a warning. */}
                <span className="font-display text-[13px] text-aurum tabular-nums shrink-0">
                  {count}
                </span>
              </div>
              <div
                className="mt-1.5 h-[5px] w-full overflow-hidden rounded-[3px] bg-white/[0.07]"
                role="img"
                aria-label={`${row.label}: ${count}`}
                data-testid="feedback-bar"
              >
                <div
                  className="h-full rounded-[3px] bg-aurum/80"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
